import auth from '@/auth/auth';
import { useFileUpload } from '@/upload/upload';
import { useFluent } from 'fluent-vue';
import { computed, ComputedRef, ref, watch, type Ref } from 'vue';
import { useRouter } from 'vue-router';
import { z } from 'zod';
import { useActiveCard } from './useActiveCard';

type ObjectFormErrors<T extends Record<string, any>> = {
    _errors: string[];
} & {
    [K in keyof T]?: { _errors: string[] };
};

type ArrayFormErrors<T extends Record<string, any>> = {
    _errors: string[];
} & {
    [index: number]: ObjectFormErrors<T>;
};

type FieldErrorValue = string[] | ArrayFormErrors<Record<string, any>>;

type ErrorsFor<T extends Record<string, any>> = {
    [K in keyof T]: T[K] extends Array<infer Item>
        ? Item extends Record<string, any>
            ? ArrayFormErrors<Item>
            : string[]
        : string[];
};

function createEmptyErrorsFromValues<T extends Record<string, any>>(initialValues: T): ErrorsFor<T> {
    return (Object.keys(initialValues) as Array<keyof T>).reduce((acc, k) => {
        const v = initialValues[k];
        acc[k] = (Array.isArray(v) ? { _errors: [] } : []) as unknown as ErrorsFor<T>[typeof k];
        return acc;
    }, {} as ErrorsFor<T>);
}

function createEmptyTouchedFromValues<T extends Record<string, any>>(initialValues: T) {
    return (Object.keys(initialValues) as Array<keyof T>).reduce(
        (acc, k) => {
            acc[k] = false;
            return acc;
        },
        {} as Record<keyof T, boolean>,
    );
}

export function useForm<S extends z.ZodObject<z.ZodRawShape>>(
    schema: S,
    initialValues: ComputedRef<Partial<Record<keyof z.infer<S>, unknown>>>,
) {
    type T = z.infer<S>;

    const fluent = useFluent();
    const router = useRouter();
    const fileUpload = useFileUpload('image/*');
    const { activeCard, setCardStatus, updateHasErrors, updateProcessing } = useActiveCard();

    const values = ref({ ...initialValues.value }) as Ref<T>;
    const errors = ref(createEmptyErrorsFromValues(initialValues.value)) as Ref<ErrorsFor<T>>;
    const touched = ref(createEmptyTouchedFromValues(initialValues.value)) as Ref<Record<keyof T, boolean>>;

    watch(
        initialValues,
        (newVals) => {
            if (!isTouched.value) {
                values.value = { ...newVals };
            }
        },
        { deep: true },
    );

    watch(fileUpload.loading, (loading) => {
        updateProcessing(loading);
    });

    async function validate(): Promise<boolean> {
        const fieldKeys = Object.keys(values.value) as Array<keyof T>;
        const results = [];
        for (const key of fieldKeys) {
            results.push(await validateField(key, true));
        }
        if (results.some((r) => r === false)) {
            return false;
        }
        return true;
    }

    async function validateField(field: keyof T, setParsedValue = false): Promise<boolean> {
        const shape = schema.shape as Record<string, z.ZodTypeAny>;
        const fieldSchema = shape[field as string];
        const isArrayField = fieldSchema instanceof z.ZodArray;

        const result = await fieldSchema.safeParseAsync(values.value[field]);

        if (result.success) {
            if (setParsedValue) {
                values.value = { ...values.value, [field]: result.data };
            }
            errors.value = { ...errors.value, [field]: isArrayField ? { _errors: [] } : [] } as ErrorsFor<T>;
            return true;
        }

        errors.value = {
            ...errors.value,
            [field]: isArrayField ? result.error.format() : result.error.format()._errors,
        } as ErrorsFor<T>;
        return false;
    }

    async function setFieldValue(field: keyof T, value: any) {
        const valueBefore = values.value[field];
        values.value[field] = value;

        if (!touched.value[field] && valueBefore !== value) {
            touched.value[field] = true;
        }

        if (touched.value[field]) {
            await validateField(field);
        }
    }

    async function setFieldTouched(field: keyof T, isTouched: boolean) {
        touched.value[field] = isTouched;
        if (isTouched) {
            await validateField(field);
        }
    }

    function fieldHasErrors(value: FieldErrorValue): boolean {
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        // ArrayFormErrors shape: check own _errors plus every indexed item
        return Object.entries(value).some(([key, v]) => {
            if (key === '_errors') return (v as string[]).length > 0;
            const item = v as ObjectFormErrors<Record<string, any>>;
            return Object.entries(item).some(([k, fieldErr]) =>
                k === '_errors'
                    ? (fieldErr as string[]).length > 0
                    : (fieldErr as { _errors: string[] })._errors.length > 0,
            );
        });
    }

    const hasErrors = computed(() => Object.values(errors.value).some(fieldHasErrors));

    watch(hasErrors, (value) => updateHasErrors(value), { immediate: true });

    const isTouched = computed(() => {
        return Object.values(touched.value).some((v) => v === true);
    });

    watch(isTouched, (value) => {
        if (activeCard.value.status !== 'View') {
            const cardStatus = value ? 'Edit-Touched' : 'Edit-Untouched';
            setCardStatus(cardStatus);
        }
    });

    const saveButtonDisabledExplanation = computed(() => {
        if (!auth.isAdmin()) return fluent.$t('no-permission-to-edit');
        if (hasErrors.value) return fluent.$t('invalid-form-fields');
    });

    function resetForm() {
        values.value = { ...initialValues.value };
        errors.value = createEmptyErrorsFromValues(initialValues.value);
        touched.value = createEmptyTouchedFromValues(initialValues.value);
    }

    function discardForm() {
        values.value = { ...initialValues.value };
        errors.value = createEmptyErrorsFromValues(initialValues.value);
        touched.value = createEmptyTouchedFromValues(initialValues.value);
    }

    function onDiscard() {
        if (isTouched.value) {
            if (!confirm(fluent.$t('leave-page-warning'))) return;
        }
        discardForm();
        router.back();
    }

    // icon upload handling

    async function uploadIcon(field: keyof T, event: Event) {
        fileUpload.abort();
        const file = await fileUpload.pickAndUpload(event);
        if (file) {
            setFieldValue(field, file);
        }
    }

    async function deleteIcon(field: keyof T) {
        fileUpload.abort();
        setFieldValue(field, '-');
    }

    const isSubmitting = ref(false);

    async function submit(action: () => Promise<void>) {
        if (isSubmitting.value) return;
        isSubmitting.value = true;
        updateProcessing(true);
        try {
            await action();
        } finally {
            isSubmitting.value = false;
            updateProcessing(false);
        }
    }

    return {
        values,
        errors,
        touched,
        hasErrors,
        isTouched,
        saveButtonDisabledExplanation,
        fileUpload,
        validate,
        setFieldValue,
        setFieldTouched,
        onDiscard,
        resetForm,
        discardForm,
        uploadIcon,
        deleteIcon,
        isSubmitting,
        submit,
    };
}
